import api from '../lib/axios'

/**
 * Spec 006 slice 6 — the old id-based URLs keep working.
 *
 *   /workspace-2/project-32/planning-delivery/project-management/ticket-812
 *   -> /web-fuel-agency/CBD-812
 *
 * These paths are in Slack threads, email, browser history and people's bookmarks. Slice 5
 * made sure nothing inside the app builds one any more; this makes the ones already out
 * there land in the right place instead of quietly aging into 404s.
 *
 * The redirect runs in the router guard rather than a route-level `redirect`, because it is
 * asynchronous: ids have to be resolved into a slug and a short code, and a ticket's
 * reference is only known to its row. The legacy route definitions stay behind it as a
 * fallback, so a tail this file fails to recognise renders the old page at the old address
 * — worse than a redirect, but not broken.
 */

/** /workspace-2/project-32/<module>/<tail> — a project-scoped legacy path. */
const LEGACY = /^\/workspace-(\d+)\/project-(\d+)\/(.*)$/

/** /workspace-2/<tail> — a workspace-scoped one, which has no project to resolve. */
const LEGACY_WORKSPACE = /^\/workspace-(\d+)\/([^/].*)$/

/**
 * Modules other than Project & Delivery. Their segment names did not change, so the rule
 * is the same for all of them: keep the module, strip the resource prefix inside it.
 *
 * `planning-delivery` is absent deliberately — it is the one module whose segment
 * disappears, and RULES below covers its tails one at a time.
 */
const MODULE_RULES = [
    [/^(brand-management|web-dev|reporting|document-studio|cyber-security|compliance|message-center|content-production)(\/|$)/, [
        [/\/repo-(\d+)$/, '/$1'],
        [/\/database-(\d+)$/, '/$1'],
        [/\/design-(\d+)$/, '/$1'],
        [/\/atlas-(\d+)$/, '/$1'],
        [/\/document-(\d+)/, '/$1'],
        [/\/report-(\d+)/, '/$1'],
        [/\/announcement-(\d+)/, '/$1'],
    ]],
]

/**
 * Workspace-scoped legacy tails. These resolve from the workspace alone, so they never
 * need a project — which is why they are matched separately.
 */
const WORKSPACE_RULES = [
    [/^incoming-tickets$/, 'incoming'],
    [/^manage-projects$/, 'projects'],
    [/^manage-projects\/new$/, 'projects/new'],
    [/^settings$/, 'settings'],
    [/^hive$/, 'hive'],
    [/^hive\/space-(\d+)\/page-(\d+)$/, 'hive/$1/page/$2'],
    [/^hive\/space-(\d+)$/, 'hive/$1'],
    [/^(web-dev|reporting|cyber-security|compliance|brand-management|document-studio|message-center|content-production)$/, '$1'],
]

/**
 * Legacy tail -> readable tail, tried in order, so the specific cases come before the
 * general ones. `null` means the segment disappears entirely.
 *
 * Two kinds of change are encoded here. Spec 005 renamed the modules — Tickets became
 * Briefs and Project Management became Tickets, which is why those two look swapped. And
 * the resource prefixes (ticket-812, brief-44, board-3) are gone: they existed to stop bare
 * numeric segments colliding, which a named parent segment already prevents.
 */
/** Sentinel: this tail is a ticket, whose address needs the row, not just the path. */
const TICKET = Symbol('ticket')

const RULES = [
    // The dashboard is the project itself, so it has no segment of its own.
    [/^dashboard$/, ''],

    // Tickets are addressed by reference now, not by a path under their project. Handled
    // separately because only the row knows its reference — see ticketRedirect below.
    [/^project-management\/ticket-(\d+)$/, null],

    [/^project-management$/, 'tickets'],
    [/^project-management\/reports$/, 'tickets/reports'],
    // No entry for project-management/settings: a route-level redirect already turns it
    // into settings/ticket-config during matching, so the guard never sees the old path.
    // The settings/ticket-config rule below then finishes the job.

    [/^tickets$/, 'briefs'],
    [/^tickets\/incoming-(\d+)$/, 'incoming/$1'],
    [/^tickets\/builder\/ticket-(\d+)$/, 'briefs/builder/$1'],
    [/^tickets\/review\/ticket-(\d+)$/, 'briefs/review/$1'],
    [/^tickets\/review\/ticket-(\d+)\/item-(\d+)$/, 'briefs/review/$1/item-$2'],
    [/^tickets\/ticket-(\d+)\/review$/, 'briefs/$1/review'],
    [/^tickets\/ticket-(\d+)$/, 'briefs/$1'],

    [/^discovery\/project-brief\/overview-(\d+)$/, 'discovery/project-brief/$1'],
    [/^feature-briefs\/review-(\d+)$/, 'feature-briefs/review/$1'],
    [/^feature-briefs\/brief-(\d+)$/, 'feature-briefs/$1'],

    [/^settings\/ticket-config$/, 'settings/tickets'],
    // A bare id under settings is a client. Must follow ticket-config, which would
    // otherwise be read as one.
    [/^settings\/(\d+)$/, 'settings/client/$1'],

    [/^design\/board-(\d+)\/screen-(\d+)\/studio$/, 'design/$1/screen/$2/studio'],
    [/^design\/board-(\d+)\/screen-(\d+)$/, 'design/$1/screen/$2'],
    [/^design\/board-(\d+)\/approval$/, 'design/$1/approval'],
    [/^design\/board-(\d+)$/, 'design/$1'],

    [/^content\/piece-(\d+)$/, 'content/$1'],
    [/^releases\/release-(\d+)$/, 'releases/$1'],

    // Modules whose name and shape did not change.
    [/^(onboarding|discovery|discovery\/project-brief|feature-briefs|settings|design|content|qa|uat|releases|links|utm|important-links)$/, '$1'],
]

/**
 * The readable path for a legacy one, or null if this is not a legacy path or cannot be
 * translated. Resolving needs the store, so it is passed in rather than imported — the
 * guard already holds it.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {ReturnType<import('../stores/workspace').useWorkspaceStore>} ws
 */
export async function readablePathForLegacy(to, ws) {
    const project = LEGACY.exec(to.path)
    if (project) return projectRedirect(to, ws, project)

    const workspace = LEGACY_WORKSPACE.exec(to.path)
    if (workspace) return workspaceRedirect(to, ws, workspace)

    return null
}

/** /workspace-2/project-32/<module>/<tail> */
async function projectRedirect(to, ws, [, wsId, projId, rest]) {
    const segment = rest.startsWith('planning-delivery/')
        ? planningDeliverySegment(rest.slice('planning-delivery/'.length))
        : otherModuleSegment(rest)

    if (segment === null) return null

    // A ticket's address is its reference, which lives on the row rather than the path.
    if (segment === TICKET) {
        return location(to, await ticketRedirect(rest))
    }

    const project = await identifiers(ws, Number(wsId), Number(projId))
    if (!project?.workspace_slug || !project?.short_code) return null

    return location(
        to,
        `/${project.workspace_slug}/${project.short_code}${segment ? `/${segment}` : ''}`,
        project.company_id,
    )
}

/** /workspace-2/<tail> — no project, so only the slug has to be resolved. */
async function workspaceRedirect(to, ws, [, wsId, tail]) {
    const rule = WORKSPACE_RULES.find(([pattern]) => pattern.test(tail))
    if (!rule) return null

    if (ws.workspaces.length === 0) {
        // Caught: this runs inside router.beforeEach, where a throw is a dead link.
        await ws.loadWorkspaces().catch(() => {})
    }

    const slug = ws.workspaces.find(w => w.id === Number(wsId))?.slug
    if (!slug) return null

    return location(to, `/workspaces/${slug}/${tail.replace(rule[0], rule[1])}`)
}

/** The Project & Delivery tails, whose module segment disappears entirely. */
function planningDeliverySegment(tail) {
    const rule = RULES.find(([pattern]) => pattern.test(tail))
    if (!rule) return null

    return rule[1] === null ? TICKET : tail.replace(rule[0], rule[1])
}

/**
 * Every other module keeps its own segment, so only the resource prefixes inside it change
 * — repo-7 becomes 7, report-3 becomes 3.
 */
function otherModuleSegment(rest) {
    const rule = MODULE_RULES.find(([pattern]) => pattern.test(rest))
    if (!rule) return null

    return rule[1].reduce((out, [pattern, to]) => out.replace(pattern, to), rest)
}

/**
 * The project behind a legacy path's project id, as the row carrying its readable
 * identifiers and its company.
 *
 * Usually already in the store — a legacy link followed from inside the app has it loaded —
 * so the request only happens when following one cold. It is also the only thing that works
 * for a link into another company: workspaces are loaded per company, so the store's own
 * lists cannot answer for one outside the active scope.
 */
async function identifiers(ws, workspaceId, projectId) {
    const loaded = ws.projects.find(p => p.id === projectId)

    if (loaded?.short_code) {
        const slug = ws.workspaces.find(w => w.id === workspaceId)?.slug
        if (slug) return { ...loaded, workspace_slug: loaded.workspace_slug ?? slug }
    }

    try {
        const { data } = await api.get(`/projects/${projectId}`)

        return data.data ?? null
    } catch {
        return null
    }
}

/**
 * A ticket named by its id. Its reference cannot be derived — reference is short_code plus
 * ref_number, and only the row carries the second half — so the server is asked.
 *
 * A failure returns null and the legacy route renders as it always did, which is the right
 * outcome for a ticket that has since been deleted or that this person cannot see.
 */
async function ticketRedirect(tail) {
    const id = tail.match(/ticket-(\d+)$/)?.[1]
    if (!id) return null

    try {
        const { data } = await api.get(`project-management/ticket/${id}/readable-location`)
        const { reference, workspace_slug: slug } = data.data ?? {}

        return reference && slug ? `/${slug}/${reference}` : null
    } catch {
        return null
    }
}

/**
 * The redirect the guard returns. Query and hash belong to the address, not to the shape of
 * it, so they carry across untouched.
 *
 * companyId rides along so the guard can switch scope before re-entering — the readable
 * routes already do this, and a legacy link into another company needs it just as much.
 */
function location(to, path, companyId = null) {
    return path ? { path, query: to.query, hash: to.hash, companyId } : null
}
